import createInstance from "../util";
import { BaseResponse } from "../common";
import { BusinessLineEnum, EnterPointSource } from "constants/base";
import { MixContractInfo, PositionLeverageLevel } from "store/entity";
import { MixContractDelegateType } from "constants/MixContractGlobal";
import { LocalCacheKey } from "constants/cacheKeys";
import { isSuccess } from "util/Common";
import Decimal from "decimal.js";
import { isBrowserEnv } from "@app/utils/constants";
import { localForage } from "@saas2/utils";
import { useParams } from "react-router-dom";
import { OriginalKlineData } from "types/kline.type";

interface PositionLevelList {
  leverageList: PositionLeverageLevel[];
}

let commonInstance = createInstance("");
let mcInstance = createInstance("v1/mcp");
let userInstance = createInstance("v1/user");

/**
 * 混合合约下单
 * @param data
 * @param header 新增secondSource表示下单的来源，0-普通，1-K线下单（包含悬浮）
 */
export function openContract(
  data: Partial<MixContractOpenParam>,
  header = { secondSource: 0, "x-security-verify-cipher": null }
): Promise<BaseResponse<any>> {
  return mcInstance.post("order/openContract", data, header);
}

/***
 * 混合合约平仓
 * @param data
 */
export function closeContract(
  data: Partial<MixContractOpenParam>,
  header = { "x-security-verify-cipher": null }
): Promise<BaseResponse<any>> {
  return mcInstance.post("order/closeContract", data, header);
}

/***
 * 当前带单 当前跟单 列表下 平仓
 * /v1/mcp/order/trader/placeContract
 */

export function placeContract(
  data: Partial<MixContractOpenParam>
): Promise<BaseResponse<any>> {
  return mcInstance.post("order/trader/placeContract", data);
}

type CancelContractParam = {
  orderId: number | string;
  tokenId: string;
  symbolId: string;
} & MixContractBase;

export function cancelContract(
  data: Partial<CancelContractParam>
): Promise<BaseResponse<any>> {
  data.secondBusinessLine = "N/A";
  return mcInstance.post("order/cancelContract", data);
}

/**
 * 按照businessLine以及对应的合约列表，组织的新接口
 */
export type TopLevelContractList = Array<{
  businessLine: BusinessLineEnum;
  symbolDTOList: MixContractInfo[];
}>;

/**
 * 获取混合合约合约列表
 */
export async function contractList(
  businessLine?: BusinessLineEnum
): Promise<BaseResponse<TopLevelContractList>> {
  const localMd5Key = LocalCacheKey.CONTRACT_LIST_MD5_KEY + businessLine;
  const localDataKey = LocalCacheKey.CONTRACT_LIST_DATA + businessLine;
  const oldMd5Value = isBrowserEnv
    ? await localForage.getItem(localMd5Key)
    : "";
  // const res: any = await mcInstance.post('ct/list2', {
  const res: any = await mcInstance.get("ct/symbols", {
    businessLine,
    md5: oldMd5Value,
  });
  let tmp = res.data?.listDTOS || [];
  const md5Value = res.data?.md5;
  if (md5Value) {
    if (oldMd5Value !== md5Value) {
      try {
        await localForage.setItem(localDataKey, JSON.stringify(tmp));
        await localForage.setItem(localMd5Key, md5Value);
      } catch {
        await localForage.removeItem(localMd5Key);
      }
    } else {
      try {
        tmp = JSON.parse(await localForage.getItem(localDataKey));
        if (!tmp) throw new Error("data cache lost");
      } catch (error) {
        await localForage.removeItem(localMd5Key);
        tmp = [];
        console.log("error: ", error);
      }
    }
  }
  return {
    ...res,
    code: res.code,
    data: tmp,
  };
}

export type BusinessLinePositionLevelList = {
  businessLine: number;
  symbolLevels: [
    {
      symbolId: string;
      levels: PositionLeverageLevel[];
    },
  ];
};

/**
 * 拉取仓位档位信息列表 - 原 list2 接口拆分
 * @param businessLine
 */
export async function fetchPositionLevelList(
  businessLine?: BusinessLineEnum
): Promise<BaseResponse<BusinessLinePositionLevelList[]>> {
  const localMd5Key = LocalCacheKey.CONTRACT_GRADIENT_MD5_KEY + businessLine;
  const localDataKey = LocalCacheKey.CONTRACT_GRADIENT_DATA + businessLine;
  const oldMd5Value = isBrowserEnv
    ? await localForage.getItem(localMd5Key)
    : "";
  const [res, coefficient]: [any, string] = await Promise.all([
    mcInstance.get("ct/gradient", {
      businessLine,
      md5: oldMd5Value,
    }),
    fetchPositionLevelCoefficient(businessLine),
  ]);
  const data = res.data as {
    lineLevels: BusinessLinePositionLevelList[];
    md5: string;
  };
  let tmp = data?.lineLevels || [];
  const md5Value = res.data?.md5;
  if (md5Value) {
    if (oldMd5Value !== md5Value) {
      try {
        await localForage.setItem(localDataKey, JSON.stringify(tmp));
        await localForage.setItem(localMd5Key, md5Value);
      } catch {
        await localForage.removeItem(localMd5Key);
      }
    } else {
      try {
        tmp = JSON.parse(await localForage.getItem(localDataKey));
        if (!tmp) throw new Error("data cache lost");
      } catch (error) {
        await localForage.removeItem(localMd5Key);
        tmp = [];
        console.log("error: ", error);
      }
    }
  }

  const coefficientDecimal = new Decimal(coefficient).add(1);
  tmp.forEach((level) => {
    level.symbolLevels.forEach((item) => {
      item.levels.forEach((level) => {
        // 乘以仓位系数
        level.startUnit = coefficientDecimal
          .times(level.startUnit)
          .truncated()
          .toNumber();
        level.endUnit = coefficientDecimal
          .times(level.endUnit)
          .truncated()
          .toNumber();
      });
    });
  });

  return {
    ...res,
    code: res.code,
    data: tmp,
  };
}

/**
 * 拉取仓位档位系数
 * @param params
 */
async function fetchPositionLevelCoefficient(
  businessLine: number
): Promise<string> {
  return "0";
  try {
    const res =
      await mcInstance.get<
        BaseResponse<{
          coefficients: { businessLine: number; coefficient: string }[];
        }>
      >("ct/coefficient");
    if (isSuccess(res.code)) {
      return (
        res.data.coefficients?.find((c) => c.businessLine === businessLine)
          ?.coefficient || "0"
      );
    }
  } catch (error) {
    console.warn(error);
  }
  return "0";
}

/**
 * 获取现货列表
 * 英国合规需要locale 参数
 */
export interface SpotAllSymbolParamsType {
  locale?: string;
}

export async function spotAllSymbol(
  params?: SpotAllSymbolParamsType
): Promise<BaseResponse<{ items: any[]; md5Value: string }>> {
  const localMd5Key = LocalCacheKey.SPOT_LIST_MD5_KEY;
  const localDataKey = LocalCacheKey.SPOT_LIST_DATA;
  const oldMd5Value = isBrowserEnv
    ? localStorage.getItem(localMd5Key) || ""
    : "";
  const res: any = await commonInstance.get(`v1/mix/symbol/getAllSymbol?md5=${oldMd5Value}&region=true&locale=${params?.locale}`);
  let items: any = res?.data || [];
  const md5Value = res.data?.md5Value;
  if (md5Value) {
    if (oldMd5Value !== md5Value) {
      try {
        const symbolDTOList = items?.items?.[0]?.symbolDTOList || [];
        // 防止后端cdn缓存数据问题，如果返回是md5值不同，且数据为空时，则使用缓存中的数据，不实用cdn
        if (symbolDTOList?.length) {
          localStorage.setItem(localDataKey, JSON.stringify(items));
          localStorage.setItem(localMd5Key, md5Value);
        } else {
          items = JSON.parse(localStorage.getItem(localDataKey));
        }
      } catch {
        localStorage.removeItem(localDataKey);
        localStorage.removeItem(localMd5Key);
      }
    } else {
      try {
        items = JSON.parse(localStorage.getItem(localDataKey));
      } catch (error) {
        console.log("error: ", error);
      }
    }
  }
  return {
    ...res,
    code: res.code,
    data: items,
  };
}

type AdjustMarginModeParam = {
  symbolId: string; // 交易对id
  afterLeverage: number;
  tokenId: string;
  businessLine: number;
  afterMarginMode: number; // 要切换的保证金模式
  longAfterLeverage: number;
  shortAfterLeverage: number;
  secondBusinessLine: string;
  userId: number | string;
};

export function adjustMarginMode(
  data: Partial<AdjustMarginModeParam>
): Promise<BaseResponse<any>> {
  data.secondBusinessLine = "N/A";

  return mcInstance.post("adjust/margin/mode", data);
}

type AdjustHoldModeParam = {
  symbolId: string;
  tokenId: string;
  businessLine: number;
  secondBusinessLine: string;
  holdMode: number;
};

/**
 * 调整保证金的持仓模式
 * @param data
 */
export function adjustHoldMode(
  data: Partial<AdjustHoldModeParam>
): Promise<BaseResponse<any>> {
  data.secondBusinessLine = "N/A";
  return mcInstance.post("adjust/hold/mode", data);
}

export type AdjustLeverageParam = {
  symbolId: string;
  tokenId: string;
  businessLine: number;
  secondBusinessLine: string;
  afterLeverage: number; // 调整后的杠杆
  holdSide: number; // 仓位方向 全仓不用
  batchMark: number; // 1为修改默认杠杆
};

/**
 * 调节杠杆
 * @param data
 */
export function adjustLever(
  data: Partial<AdjustLeverageParam>
): Promise<BaseResponse<any>> {
  data.secondBusinessLine = "N/A";
  return mcInstance.post("adjust/lever", data);
}

/**
 * 变更仓位自动追加保证金
 * @param data
 */
export function adjustAutoAppend(
  data: Partial<
    {
      tokenId: string;
      symbolId: string;
      holdSide: number;
      autoAppendMargin: number;
    } & MixContractBase
  >
) {
  data.secondBusinessLine = "N/A";
  return mcInstance.post("adjust/auto/append", data);
}

/**
 * 手动调整保证金
 * @param data
 */
export function adjustChangeMargin(
  data: Partial<
    {
      tokenId: string;
      symbolId: string;
      holdSide: number;
      amount: number | string; // 注意：通过该值的正负来确定追加情况
    } & MixContractBase
  >
): Promise<BaseResponse<any>> {
  data.secondBusinessLine = "N/A";
  return mcInstance.post("adjust/change/margin", data);
}

export type FeeRate = {
  capitalRate: number;
  settleTime: number;
  symbolCode: string;
  symbolDisplayName: string;
};

/**
 * 查询某合约的资金费率情况
 * @param data
 */
export function marketCapitalFeeRateList(data: {
  businessLine: BusinessLineEnum;
  symbolId: string;
  pageNo: number;
  pageSize: number;
}): Promise<BaseResponse<FeeRate[]>> {
  return mcInstance.post("market/capital/feeRate/list", data);
}

/**
 * 按时间范围查询资金结算费率
 * https://yapi.sniper5.vip/project/32/interface/api/79723
 * post /v1/mcp/market/settle/feeRate/list
 *    "businessLine":10,
 *     "symbolId": "BTCUSDT_UMCBL",
 *     "startTime": 1708498705000,
 *     "endTime": 1711004305588
 * */
export function marketSettleFeeRateList(data: {
  businessLine: BusinessLineEnum;
  symbolId: string;
  range: "7d" | "1m";
}): Promise<BaseResponse<FeeRate[]>> {
  // 根据 range 计算 startTime 和 endTime
  const now = Date.now();
  let startTime = 0;
  let endTime = now;
  if (data.range === "7d") {
    startTime = now - 7 * 24 * 60 * 60 * 1000;
  } else if (data.range === "1m") {
    startTime = now - 30 * 24 * 60 * 60 * 1000;
  }
  const params = {
    businessLine: data.businessLine,
    symbolId: data.symbolId,
    startTime,
    endTime,
  };

  return mcInstance.post("market/settle/feeRate/list", params);
}

/**
 * 风险准备金流水
 */
export type RiskCapitalRecord = {
  id: string;
  symbolId: string;
  tokenId: string;
  available: string;
  changed: string;
  updateTime: string | number;
  businessLine: string;
  symbolDisplayName: string;
  remain: string;
};

/**
 * 风险准备金余量
 * @param data
 */
export function configInfoRiskCapitalAmount(data: {
  businessLine: BusinessLineEnum;
  symbolId: string;
  tokenId: string;
}): Promise<BaseResponse<string>> {
  return mcInstance.post("ct/configInfo/riskCapitalAmount", data);
}

/**
 * 风险准备金流水
 * @param data
 */
export function configInfoRiskCapitalFlow(data: {
  businessLine: BusinessLineEnum;
  symbolId: string;
  tokenId: string;
  fromId: string;
  size: number;
}): Promise<BaseResponse<RiskCapitalRecord[]>> {
  return mcInstance.post("ct/configInfo/riskCapitalFlowV2", data);
}

/**
 * 交割数据item
 */
export type DeliveryDataItem = {
  createTime: number;
  deliveryPrice: string;
  deliveryTime: number;
  id: string;
  symbolName: string;
};

export type DeliveryDataRecord = {
  items: DeliveryDataItem[];
  hasNextPage: boolean;
  pageNo: number;
  pageSize: number;
  pages: number;
  total: number;
};

/**
 * 交割数据
 * @param data
 */
export function fetchDeliveryData(data: {
  // businessLine: BusinessLineEnum
  symbolFlag: string;
  pageNo: number;
  pageSize: number;
}): Promise<BaseResponse<DeliveryDataRecord>> {
  return mcInstance.post("ct/delivery/list", data);
}

/**
 * 普通委托 批量撤单
 * @param data
 */
export function batchCancelContract(data: {
  businessLine: BusinessLineEnum;
  symbolIdList: string[];
  tokenId?: string;
  secondBusinessLine?: string;
}): Promise<BaseResponse<any>> {
  data.secondBusinessLine = "N/A";
  return mcInstance.post("order/batchCancelContract", data);
}

/**
 * 普通委托 更新预设止盈止损
 * @param data
 */
export function updateContractPreset(data: {
  businessLine: BusinessLineEnum;
  symbolId: string;
  tokenId: string;
  orderId: string;
  presetTakeProfitPrice: string;
  presetStopLossPrice: string;
  secondBusinessLine?: string;
  exeTakeProfitPrice?: string;
  exeStopLossPrice?: string;
  presetTppTriType?: number;
  presetSlpTriType?: number;
}): Promise<BaseResponse<any>> {
  data.secondBusinessLine = "N/A";
  return mcInstance.post("order/updateContractPreset", data);
}

export type PageData = {
  symbolDisplayName: string;
  symbolHoldRatio: string;
  limitAmpFactor?: string;
  busLine?: number;
};

// /v1/mcp/ct/ratio
/**
 * 获取交易对-单一用户持仓比例
 */
export function contractRatio(): Promise<BaseResponse<PageData[]>> {
  return mcInstance.post("ct/ratio");
}

/***
 * 一键反手
 */
// /v1/mcp/backContract

export function backContract(
  data: {
    businessLine: BusinessLineEnum;
    symbolId: string;
    tokenId: string;
    cancelOrder: boolean; // 如果为true， 表示包含已有委托，所以需要撤单；      如果为false, 表示不包含已有委托，所以后续无须撤单
    delegateType: MixContractDelegateType; // 委托类型
    delegateCount: string; // 委托数量
  },
  header = { "x-security-verify-cipher": null }
): Promise<BaseResponse<any>> {
  let newData = Object.assign({}, data, {
    enterPointSource: EnterPointSource.WEB,
    secondBusinessLine: "N/A",
  });
  return mcInstance.post("order/backContract", newData, header);
}

/**
 * 获取合约信息-我的费率
 */
export function getLevelfellWidthTrade(data: { symbolId: string }): Promise<
  BaseResponse<{
    taker: string;
    maker: string;
  }>
> {
  return commonInstance.post(`v1/mix/getCurrentRate`, data);
}

/**
 * 获取机构用户数据
 */
export function getVipFeeData(): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mix/enterpirseUser/detail");
}

type KlineHistoryQueryParam = {
  limit: number; // 条数限制
  kLineStep: string; // 支持的step:1,5,15,30,60,240,720,1440,10080,单位：分钟。60对应小时线
  endTime: string; // 分段数据的终点，时间戳
  kLineType: number; // k线类型 1、k线     2、合理标的价格k线 带~    3、指数
  symbolId: string;
};

/**
 * 获取分段K线数据
 * @param param
 */
export function getMoreKlineData(
  param: KlineHistoryQueryParam
): Promise<BaseResponse<OriginalKlineData[]>> {
  return commonInstance.post("v1/kline/getMoreKlineData", param);
}

/**
 * 全部平仓
 * @param param
 */
export function closeAll(
  param: {
    businessLine: BusinessLineEnum;
    tokenId?: string;
  },
  header = { "x-security-verify-cipher": null }
): Promise<BaseResponse<any>> {
  let newData = Object.assign({}, param, {
    enterPointSource: EnterPointSource.WEB,
    secondBusinessLine: "N/A",
  });
  return commonInstance.post("v1/mcp/order/closeAll", newData, header);
}

/**
 * 取消类型枚举 0 止盈+止损；1 止盈；2 止损
 */
type CancelTypeEnum = 0 | 1 | 2;

/**
 * 一键止盈止损
 * @param {number} cancelType 0 止盈+止损；1 止盈；2 止损
 * @param {Array<number>} businessLineList 业务线 List
 */
export function cancelALLTrackingOrder(data: {
  cancelType: CancelTypeEnum;
  businessLineList: Array<number>;
}): Promise<BaseResponse<any>> {
  return commonInstance.post(
    "v1/trigger/tracking/cancelALLTrackingOrder",
    data
  );
}

/**
 * 一键平仓
 * @param {Array<number>} businessLineList 业务线 List
 */
export function closeALLTrackingOrderV1(data: {
  businessLineList: Array<number>;
}): Promise<BaseResponse<any>> {
  return commonInstance.post(
    "v1/trigger/tracking/closeALLTrackingOrderV1",
    data
  );
}

/**
 * 追踪委托  开仓
 */
export function trackOrderOpen(
  data,
  header = { "x-security-verify-cipher": null }
): Promise<BaseResponse<any>> {
  return commonInstance.post(
    "v1/trigger/tracking/plan/placePlan",
    data,
    header
  );
}

/**
 * 分段订单 开仓
 * */

type scaledOrderParam = {
  tokenId: string;
  symbolId: string;
  businessLine: number;
  secondBusinessLine: string;
  businessSource: number;
  enterPointSource: number;
  delegateCount: string;
  orderType: number;
  delegateType: number;
  pricedSymbol: string;
  timeInForceValue: number;
  delegatePrice: string;
  languageType: number;
};
export function segmentOrderOpen(
  data: scaledOrderParam[],
  header = { "x-security-verify-cipher": null }
): Promise<BaseResponse<any>> {
  return commonInstance.post(
    "v1/mcp/order/segmentContract",
    {
      placeContractParams: data,
    },
    header
  );
}

export interface PlaceStrategyIceData {
  businessLine: number; // 业务线
  delegateType: string; // 委托类型 1-开多 2-开空 3-平多 4-平空
  marginMode: string; // 保证金模式。1:逐仓 2:全仓
  holdMode: string; // 持仓模式 单向1 双向2
  delegateCount: string; // 委托数量
  tokenId: string; // 保证金币种
  symbolId: string; // 币对Id
  splitType: string; // 拆单类型1:数量、2:笔数
  splitValue: number; // 拆单类型配合值
  dealType: number; // 成交类型 1:更快成交，2:更优价格(比例)，3更优价格(金额)4：固定价格
  dealValue: number; // 成交类型值
  limitPrice: number; // 限制价格
  leverage: number; // 杠杆倍数
  onlylow: number; // 只减仓 0否、1是
}

/**
 * 冰山 下单
 *
 * https://yapi.sniper5.vip/project/53/interface/api/98754
 * /v1/trigger/strategy/ice/placeStrategyIce
 *
 * */
export function placeStrategyIce(
  data: PlaceStrategyIceData
): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/trigger/strategy/ice/placeStrategyIce", data);
}

/**
 * 获取冰山配置
 * /v1/trigger/strategy/ice/query/queryConfig
 * */
export function queryIceConfig(): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/trigger/strategy/ice/query/queryConfig");
}

/**
 * 价格提醒配置查询
 */
export interface IPriceData {
  id?: number;
  businessLine: number;
  tabCoin: any;
  configurePricePercentage: number;
  isMarketPrice: number; //0:标记价格  1：市场价格
  exchangeId: string;
  exchangeName: string;
  alertType: number;
  configurePrice: number;
  isOpen?: number;
  frequency: number;
  configurePriceUp?: number;
  configurePriceDown?: number;
  remindType: Array<string>;
}

export interface IPriceDataRequest {
  alertConfigDtoList: Array<IPriceData>;
  businessConfigNumber: string | number;
  businessLine: number;
}

/**
 * 价格提醒配置查询(最多50)
 * @param data
 * @returns
 */
export function getPriceRemindConfigApi(data: {
  businessLines: Array<number>;
}): Promise<
  BaseResponse<{
    IPriceDataRequest;
  }>
> {
  return commonInstance.post(`v1/send/priceremindconfig/select`, data);
}

/**
 * 滑杆步长最大值，最小值 获取步长配置接口
 */
export function getSettingSlider(data: {
  businessLine: number;
  exchangeId: string;
  exchangeName: string;
  leftCoinName: string;
  rightCoinName: string;
}): Promise<
  BaseResponse<{
    exchangeId: string;
    exchangeName: string;
    minVal: number;
    maxVal: number;
    minPer: number;
    maxPer: number;
    stepVal: number;
    stepPer: number;
    pushSwitch: boolean;
    stationSwitch: boolean;
    popSwitch: boolean;
  }>
> {
  return commonInstance.post(
    `v1/send/priceremindconfig/exchange/setting`,
    data
  );
}

/**
 *  模块类型
 * 0  全局可以 1福利中心； 2 邀请好友 3 合约仓位分享图 4 跟单仓位分享图； 5  策略收益分享图； 6  跟单分享图-交易员 7 分享APP  8截图 9 红包封面
 * 69 盘前交易
 */
export enum BizTypeEnum {
  WELFARE = 1,
  INVITE = 2,
  CONTRACT_SHARE = 3,
  FOLLOW_SHARE = 4,
  STRATEGY_SHARE = 5,
  FOLLOW_SHARE_TRADER = 6,
  SHARE_APP = 7,
  SCREENSHOT = 8,
  REDPACKET_COVER = 9,
  PRE_MARKET = 69,
}

/**
 * 查询分享信息
 */
export function getShareInfoApi(data: {
  bizType: BizTypeEnum;
}): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mix/share/common", data);
}

// 领取利益点任务卡
export function getTaskCardApi(data: {
  activityId: number;
  interestId: number;
}): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/act/task/card/user/click/join", data);
}

/**
 * 合约-历史订单列表
 */
export function getContractHistoryOrderListApi(
  data
): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mcp/order/getHistoryOrderList", data);
}

/**
 * 合约-成交明细
 */
export function getContractDealRecordListApi(data): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mcp/order/getDealRecordList", data);
}

/**
 * 合约-财务记录筛选 options 数据
 */
export function getContractFinancialQueryConditionsApi(
  data
): Promise<BaseResponse<any>> {
  return commonInstance.post(
    "v1/mcp/financial/getFinancialQueryConditions",
    data
  );
}
/**
 * 合约-财务记录
 */
export function getContractFinancialListApi(data): Promise<BaseResponse<any>> {
  return commonInstance.post("v1/mcp/financial/getFinancialList", data);
}

/**
 * 查询某笔订单的成交明细
 */

export function getContractOrderDetailApi(data): Promise<BaseResponse<any>> {
  return commonInstance.post("inner/v1/mcp/order/getOrderInfo", data);
}

interface ModifyOrderApiData {
  orderId: string; // 订单id
  businessLine: BusinessLineEnum; // 业务线
  updateDelegateCount: number; // 修改数量
  updateDelegatePrice: number; // 修改价格
  newAppVersion?: boolean; //为了兼容老版本，新修改的需要传true
  // https://bglimited.larksuite.com/wiki/YKH6wIC0WiyaGRkMf9xu5YrOsLc
}

/**
 * 限价单修改
 */

export const modifyOrder = (
  data: ModifyOrderApiData
): Promise<BaseResponse<unknown>> => {
  return commonInstance.post("v1/mcp/order/modifyOrder", data);
};

export type EtfElementData = {
  iconUrl: string;
  baseTokenId: string;
  businessType: string;
  groupId: number;
  quoteTokenId: string;
  symbolCode: string; // 成分交易对，用于获取对应ticker价格
  symbolId: string;
  weight: string; // 权重
  tickerPrice?: string; // 成分交易对价格
};

/**
 * 获取etf合约组成成分
 * @param data
 */
export function getEtfElementInfo(data: {
  symbolId: string;
}): Promise<
  BaseResponse<{ etfElementList: EtfElementData[]; symbolId: string }>
> {
  return commonInstance.post("v1/mix/public/getEtfElementInfo", data);
}

export function getTickerList(data: {
  businessLine: number;
}): Promise<BaseResponse<any[]>> {
  return commonInstance.post("v1/mcp/market/ticker", data);
}

export type OpenPriceMap = {
  [key: string]: {
    openToday: string;
  };
};

type OpenPriceListResponseBody = {
  timezone: number;
  bussiness: string;
  dateTime: string;
  symbolKlines: OpenPriceMap;
};

/**
 * 获取某业务线某时区各个交易对的开盘价
 * @param data
 */
export function getOpenPrice(data: {
  timezone: number;
  business: string;
}): Promise<BaseResponse<OpenPriceListResponseBody>> {
  return commonInstance.post("v1/kline/getOpenPrice", {
    bussiness: data.business,
    timezone: data.timezone,
  });
}

export interface CategoryData {
  id: number; //板块id
  name: string; //板块名称
}
/**
 * 获取业务线板块映射关系
 * @param data
 */
export function getCategories(): Promise<BaseResponse<CategoryData>> {
  return commonInstance.post("v1/mcp/ct/queryCategories", {});
}

export interface OrderQueryConditionsParams {
  businessLine: number | string;
  languageType: number | string;
}

export interface SymbolItem {
  baseTokenId: string;
  businessLine: number;
  quoteTokenId: string;
  symbolDisplayName: string;
  symbolId: string;
}

export interface TokenItem {
  tokenDisplayName: string;
  tokenId: string;
}

export interface OrderQueryConditionsResponse {
  businessSourceList: {
    businessSource: number;
    businessSourceDesc: string;
  }[];
  symbolList: SymbolItem[];
  tokenList: TokenItem[];
}

export interface TraderProAlertResponse {
  applyPass: boolean;
  violation: boolean;
  id: string;
  violation30Day: boolean;
  applyReject: boolean;
  traderProOnline: boolean;
  params?: [];
}

/**
 * 修改是否自动追加保证金
 */

export const modifyAutoAppendMargin = (
  data: ModifyAutoAppendMarginData
): Promise<BaseResponse<unknown>> => {
  return commonInstance.post("v1/mcp/adjust/auto/append", data);
};

export function switchDemoAccount() {
  return commonInstance.post("v1/user/sub/switchLoginDemo");
}
/**
 * 混合合约订单查询条件
 * @param data
 */
interface ModifyAutoAppendMarginData {
  businessLine: BusinessLineEnum; // 业务线
  tokenId: string;
  symbolId: string;
  holdSide: number;
  autoAppendMargin: number; // 0-不自动追加,1-自动追加
  accountId?: string;
}

// 创世计划 demo 账户 主账户来回切换接口
export function switchCreationDemoAccount() {
  return userInstance.post("/sub/switchTradeDemo");
}

// 查询子账户列表
export function getTradeProUserList() {
  return commonInstance.post("v1/trigger/trader-pro/user-list");
}

// 查询子账户违规状态
export function getTradeProAlert(): Promise<
  BaseResponse<TraderProAlertResponse>
> {
  return commonInstance.post("v1/trigger/trader-pro/alert");
}

// 重置创世计划demo账户资金状态
export function getTradeProReset(param): Promise<BaseResponse<unknown>> {
  return commonInstance.post("v1/trigger/trader-pro/reset", param);
}

export type mmrStopLossParam = {
  tokenId: string;
  symbolId: string;
  lossMMRValue: number;
  delegateType: MixContractDelegateType;
  pricedSymbol: string;
  lossUpdate: boolean; //true:更新,false:创建
  planId: string; // 更新时传入计划id
  businessLine: BusinessLineEnum;
  secondBusinessLine: string;
  planType: number; // 0-普通计划 1-止盈计划 2-止损计划 3-移动止盈止损 4-仓位止盈 5-仓位止损 6-追踪委托 7-mmr止损
  enterPointSource: EnterPointSource;
};

/** mmr止损
 * https://yapi.sniper5.vip/project/44/interface/api/75293
 *
 * */

export function mmrStopLoss(
  data: mmrStopLossParam,
  header = { "x-security-verify-cipher": null }
): Promise<BaseResponse<unknown>> {
  data.secondBusinessLine = "N/A";
  data.enterPointSource = EnterPointSource.WEB;

  return commonInstance.post(
    "v1/trigger/plan/mmr/placePlanOrder",
    data,
    header
  );
}

/**
 * 获取mmr设置范围
 * https://yapi.sniper5.vip/project/44/interface/api/75506
 * */
export function getMmrRiskRatioRange(): Promise<BaseResponse<unknown>> {
  return commonInstance.post("v1/trigger/plan/mmr/getRiskRatioRange");
}

/**
 * 获取合约公共配置接口
 */
export function queryContractCommonConfig(): Promise<
  BaseResponse<{ defaultLever: string; supportBusLines: number[] }>
> {
  return mcInstance.post("ct/queryContractCommonConfig");
}

export const getContractSymbols = (): Promise<
  BaseResponse<{
    listDTOS: {
      businessLine: BusinessLineEnum;
      symbolDTOList: MixContractInfo[];
      tokenIds: string[];
    }[];
  }>
> => {
  return mcInstance.get("ct/symbols");
};
